import { useEffect, useState } from 'react';
import type { Tag } from '@mlc/shared';
import { listTags } from './api';
import {Icon, TAG_TONE_CLASSES, fieldClasses } from './ui';

/**
 * Étiquettes manuelles **de ce membre** sur la recette (US-21, personnelles
 * depuis US-51).
 *
 * US-21 l'avait posé sur l'écran d'édition, avec ce raisonnement : « poser une
 * étiquette est une modification de la recette au même titre qu'un
 * ingrédient ». Ce n'est plus vrai. Une étiquette manuelle vit dans
 * `recipe_member_tag` : elle dit ce que **quelqu'un** pense de la recette, pas
 * ce que la recette est. Le composant a donc quitté `RecipeDetail.tsx` pour son
 * propre fichier, et sert les deux écrans — l'édition, réservée au
 * propriétaire, et la fiche en lecture, ouverte à qui voit la recette.
 *
 * Le principe d'US-14 tient toujours : la fiche en lecture n'ouvre pas de champ
 * de saisie de la recette. Une étiquette personnelle n'en est pas un.
 *
 * Les étiquettes **calculées** apparaissent à côté, en lecture seule et
 * visuellement distinctes : les voir sans pouvoir les toucher est plus clair
 * que les masquer — sinon leur apparition après un enregistrement passe pour
 * un bug (§2 CLAUDE.md, « recalculés, jamais saisis »).
 */
export function TagsEditor({
  tags,
  onChange,
}: {
  tags: Tag[];
  onChange: (names: string[]) => Promise<void>;
}): React.JSX.Element {
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  // Dépendance sur une **clé stable** et non sur le tableau lui-même : `tags`
  // est une nouvelle référence à chaque rendu du parent, si bien qu'un effet
  // qui en dépend directement relance une requête à chaque frappe ailleurs
  // dans l'écran.
  const tagsKey = tags.map((tag) => tag.id).join(',');

  useEffect(() => {
    const controller = new AbortController();
    listTags(controller.signal)
      .then((all) => { setSuggestions(all.filter((tag) => tag.kind === 'manual')); })
      .catch(() => { /* les suggestions sont un confort : leur absence n'empêche pas de taper un nom */ });
    return () => { controller.abort(); };
  }, [tagsKey]);

  const manual = tags.filter((tag) => tag.kind === 'manual');
  const auto = tags.filter((tag) => tag.kind === 'auto');

  function apply(names: string[]): void {
    setBusy(true);
    void onChange(names).finally(() => { setBusy(false); setDraft(''); });
  }

  function add(name: string): void {
    const cleaned = name.trim();
    if (cleaned === '') return;
    // Comparaison insensible à la casse et aux accents, comme le serveur :
    // sinon « Été » créerait un doublon visuel de « été ».
    const already = manual.some((tag) => tag.name.localeCompare(cleaned, 'fr', { sensitivity: 'base' }) === 0);
    if (already) { setDraft(''); return; }
    apply([...manual.map((tag) => tag.name), cleaned]);
  }

  return (
    <section aria-label="Étiquettes" className="flex flex-col gap-2">
      <p className="text-sm text-foreground-secondary">Étiquettes</p>
      <div className="flex flex-wrap items-center gap-2">
        {manual.map((tag) => (
          <span key={tag.id} className={`flex items-center gap-1 rounded-full pl-3 ${TAG_TONE_CLASSES.manual}`}>
            {tag.name}
            <button
              type="button"
              disabled={busy}
              aria-label={`Retirer l’étiquette ${tag.name}`}
              onClick={() => { apply(manual.filter((other) => other.id !== tag.id).map((other) => other.name)); }}
              className="size-touch rounded-full"
            >
              <Icon name="croix" />
            </button>
          </span>
        ))}

        <label className="sr-only" htmlFor="tag-draft">
          Ajouter une étiquette
        </label>
        <input
          id="tag-draft"
          list="tag-suggestions"
          value={draft}
          disabled={busy}
          placeholder="Ajouter une étiquette"
          onChange={(e) => { setDraft(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(draft); }
          }}
          onBlur={() => { add(draft); }}
          className={fieldClasses('compact', 'w-48')}
        />
        <datalist id="tag-suggestions">
          {suggestions.map((tag) => (
            <option key={tag.id} value={tag.name} />
          ))}
        </datalist>
      </div>

      {auto.length > 0 && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-foreground-muted">
          <span>Calculées :</span>
          {auto.map((tag) => (
            <span key={tag.id} className={`rounded-full px-3 py-1 ${TAG_TONE_CLASSES.auto}`}>
              {tag.name}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}
